import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Tabs,
  Tab,
  Typography
} from '@mui/material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import InventoryIcon from '@mui/icons-material/Inventory2'
import RecordIcon from '@mui/icons-material/Assessment'
import SettingsIcon from '@mui/icons-material/Settings'
import LogoutIcon from '@mui/icons-material/Logout'
import SchoolIcon from '@mui/icons-material/School'
import DataIcon from '@mui/icons-material/BarChart'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import LiveTvIcon from '@mui/icons-material/LiveTv'
import mapitLogo from '../../utilities/dummy_assets/mapITlogo.png'
import mapitfullLogo from '../../utilities/dummy_assets/svgs/Mapiot_whitetext.svg'
import MSISLogo from '../../utilities/dummy_assets/svgs/LOGO_MSIS_Dark.svg'
import smallMSISLogo from '../../utilities/dummy_assets/svgs/LOGO_MSIS_Dark_Small.svg'
import expandRight from '../../utilities/dummy_assets/Expand_left_double.png'
import expandLeft from '../../utilities/dummy_assets/Expand_left_double (1).png'
import { logoutUser } from '../../redux/slices/userSlice'
import GlobalWebSocketService from '../../utilities/api/IMS/GlobalWebSocketService'

// Define the default tabsConfig
const defaultTabsConfig = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
  { label: 'Training', icon: <SchoolIcon />, path: '/training' },
  { label: 'Project', icon: <FolderOpenOutlinedIcon />, path: '/project' },
  { label: 'Live Feed', icon: <LiveTvIcon />, path: '/tl-live' },
  { label: 'Records', icon: <RecordIcon />, path: '/records' },
  { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  { label: 'Logout', icon: <LogoutIcon />, path: '/login', isLogout: true }
]

// Define the admin tabsConfig
const adminTabsConfig = [
  { label: 'TL Data', icon: <DataIcon />, path: '/tl-data' },
  { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  { label: 'Logout', icon: <LogoutIcon />, path: '/login', isLogout: true }
]

const CustomTab = ({ label, icon, selected, collapsed }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: collapsed ? 'center' : 'flex-start',
      alignItems: 'center',
      width: collapsed ? { xs: '80%', sm: '70%', md: '60%' } : '90%',
      margin: '0 auto',
      padding: collapsed
        ? { xs: '8px 4px', md: '10px 6px' }
        : { xs: '6px 8px', sm: '8px 10px', md: '10px 12px' },
      borderRadius: '10px',
      backgroundColor: selected ? '#FFFFFF' : 'transparent',
      color: selected ? 'black' : 'white',
      gap: collapsed ? 0 : { xs: '8px', sm: '10px', md: '12px' },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor: 'white',
        color: 'black',
        '& .MuiSvgIcon-root': { fill: 'black' },
      },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '.MuiSvgIcon-root': {
          fill: selected ? 'black' : 'white',
          fontSize: { xs: '20px', sm: '22px', md: '24px' },
        },
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="body1"
      sx={{
        fontFamily: 'Roboto, sans-serif',
        fontSize: { xs: '14px', sm: '16px', md: '20px' },
        fontWeight: 400,
        textAlign: 'left',
        opacity: collapsed ? 0 : 1,
        width: collapsed ? 0 : 'auto',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const MenuComponent = ({ collapsed = false, setCollapsed = () => { } }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useSelector((state) => state.user.user)
  const [selectedTab, setSelectedTab] = useState(null)
  const [isTransitioning, setTransitioning] = useState(false)

  const tabsConfig = user.user.TL_name === 'MBF Admin' ? adminTabsConfig : defaultTabsConfig
  console.log('user: ', user)
  useEffect(() => {
    const foundTab = tabsConfig.findIndex((tab) => tab.path === location.pathname)
    if (foundTab !== -1) {
      setSelectedTab(foundTab)
    }
  }, [location.pathname, tabsConfig])

  const handleTabChange = (event, newValue) => {
    const selected = tabsConfig[newValue]

    setTransitioning(true)
    setTimeout(() => {
      setSelectedTab(newValue)
      if (selected.isLogout) {
        GlobalWebSocketService.disconnect()
        dispatch(logoutUser())
      }
      navigate(selected.path)
      setTransitioning(false)
    }, 300)
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: {
            xs: collapsed ? '60px' : '220px',
            sm: collapsed ? '70px' : '240px',
            md: collapsed ? '7%' : '17.5%',
            lg: collapsed ? '7%' : '17.5%',
          },
          backgroundColor: '#212121',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          zIndex: 1000,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Centered Logo + Toggle Button */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: collapsed ? 'column-reverse' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              py: { xs: 1.5, sm: 2, md: 2.5 },
              px: collapsed ? 0 : { xs: 1, sm: 1.5, md: 2 },
              position: 'relative',
              gap: collapsed ? { xs: '12px', md: '16px' } : '0',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {/* MSIS Logo - Always Centered */}
            <a
              href="https://makerbhavanfoundation.org/"
              style={{ display: 'block', flex: 1 }}
            >
              <Box
                component="img"
                src={collapsed ? smallMSISLogo : MSISLogo}
                alt="MSIS Logo"
                sx={{
                  height: {
                    xs: collapsed ? '40px' : '60px',
                    sm: collapsed ? '60px' : '80px',
                    md: collapsed ? '80px' : '100px',
                  },
                  width: 'auto',
                  maxWidth: collapsed ? '90%' : '85%',
                  objectFit: 'contain',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: 1,
                }}
              />
            </a>

            {/* Toggle Button - Positioned at Top-Right */}
            <Box
              onClick={() => setCollapsed(!collapsed)}
              sx={{
                width: { xs: 28, sm: 30, md: 32 },
                height: { xs: 28, sm: 30, md: 32 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: '8px',
                flexShrink: 0,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <img
                src={collapsed ? expandRight : expandLeft}
                alt="Toggle sidebar"
                style={{ width: '16px', height: '16px' }}
              />
            </Box>
          </Box>
          <Tabs
            orientation="vertical"
            variant="scrollable"
            scrollButtons={false}
            value={false}
            onChange={() => { }}
            sx={{
              width: '100%',
              px: { xs: 0.5, md: 1 },
              '& .MuiTab-root': {
                alignItems: 'center',
                justifyContent: 'center',
                textTransform: 'none',
                margin: { xs: '2px 0', md: '3px 0' },
                padding: { xs: '4px 2px', sm: '6px 4px', md: '8px 6px' },
                backgroundColor: 'transparent',
                minWidth: 'auto',
                width: '100%',
                minHeight: { xs: '40px', sm: '44px', md: '48px' },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'white',
                  color: 'black',
                },
              },
              '& .MuiTabs-flexContainer': {
                flexDirection: 'column',
              },
              flex: 1,
            }}
          >
            {tabsConfig.map((tab, index) => (
              <Tab
                key={tab.label}
                label={
                  <CustomTab
                    label={tab.label}
                    icon={tab.icon}
                    selected={selectedTab === index}
                    collapsed={collapsed}
                  />
                }
                disableRipple
                onClick={() => handleTabChange(null, index)}
              />
            ))}
          </Tabs>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: '2px', md: '4px' },
            p: { xs: '5px', sm: '8px', md: '10px' },
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              color: '#FFF',
              fontFamily: 'Roboto',
              fontSize: { xs: '10px', sm: '12px', md: '14px' },
              fontWeight: 400,
              fontStyle: 'italic',
              textTransform: 'lowercase',
              opacity: collapsed ? 0 : 0.8,
              height: collapsed ? 0 : 'auto',
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            powered by
          </Typography>
          <Box
            sx={{
              width: collapsed
                ? { xs: '28px', sm: '32px', md: '36px' }
                : { xs: '55px', sm: '70px', md: '90px' },
              height: collapsed
                ? { xs: '28px', sm: '32px', md: '36px' }
                : { xs: '36px', sm: '45px', md: '55px' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <img
              src={collapsed ? mapitLogo : mapitfullLogo}
              alt="MapIT Logo"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MenuComponent;